import React, { PureComponent } from 'react'
import { observer } from 'mobx-react'
import { makeObservable, observable } from 'mobx'
import AnimateHeight from 'react-animate-height'
import i18n from 'i18next'
import * as R from 'ramda'

import './panel-accordion.scss'
import Menu from '../../UniformComponents/Menu'
import HoverInfo from '../../UniformComponents/HoverInfo'
import Input from '../form/Input'
import Tooltip from '../../UniformComponents/Tooltip'
import Button from '../../UniformComponents/Button'
import GrabIcon from '../../assets/images/move-icon.svg'

const pageMenuActions = Object.freeze({
    LOCK: 'lock',
    DELETE: 'delele',
    SKIP: 'skip',
    TEMPLATE: 'template',
    CHARTS: 'charts',
    PREVIEW: 'preview',
    REDIRECT: 'surveyRedirect',
    SHOW: 'show',
})

const PageMenu = observer(({ menuOptions, isSurveyClosed }) => (
    <div className="survey-builder-page-options-container pagemenu">
        <div className="options-container">
            {/* exploriAdmins can interact with closed surveys only to map responses */}
            {isSurveyClosed ? null : (
                <Menu
                    cssClass="option-menu"
                    list={menuOptions}
                    minWidth={300}
                    dir="left"
                    hasUniqueLast
                >
                    <div className="menu-icon">
                        <i className="fa-regular fa-ellipsis-vertical" />
                    </div>
                </Menu>
            )}
        </div>
    </div>
))

const RedirectMenu = observer(({ that, menuOptions, isFirstExitPage }) => {
    const {
        page,
    } = that.props
    const isCompletionPage = page?.pageType === 'CO'
    return (
        <div className="survey-builder-page-options-container redirectmenu">
            {page.redirectUrlSettings.redirectUrl
                && (
                    <div className="exit-page-redirect">
                        <Tooltip text={i18n.t('settings.kiosk.redirectTooltip', { seconds: page.redirectUrlSettings.delayRedirectSec, url: page.redirectUrlSettings.redirectUrl })} direction="bottom" />

                        <i className="fal fa-globe panel-accordion-redirect-icon" />
                    </div>
                )}
            <div className="options-container">
                <Menu
                    cssClass="option-menu"
                    list={menuOptions}
                    minWidth={300}
                    dir="left"
                    hasUniqueLast={!isCompletionPage || !isFirstExitPage}
                >
                    <div className="menu-icon">
                        <i className="fa-regular fa-ellipsis-vertical" />
                    </div>
                </Menu>
            </div>
        </div>
    )
})

@observer
class PanelAccordion extends PureComponent {
    @observable menuOpen = false

    constructor(props) {
        super(props)
        makeObservable(this)

        const { subtitle } = this.props
        this.state = {
            pageName: subtitle,
        }
    }

    toggleIsEditingSubtitle = (val) => {
        if (val) this.isEditingSubtitle = val
        else this.isEditingSubtitle = !this.isEditingSubtitle
    }

    toggleMenu = (val) => {
        if (typeof val === 'boolean') this.menuOpen = val
        else this.menuOpen = !this.menuOpen
    }

    execMenuAction = (func) => {
        this.menuOpen = false
        func()
    }

    getMenuItemConfig = (key, page) => {
        const { store, options } = this.props
        const item = { isDisabled: false, disabledReasons: [], tooltip: '', isVisible: true }
        const isDefaultPage = !!options && options === 'page'
        const isLocked = R.pathOr(false, ['isLocked'], page)
        const isAdmin = store?.isExploriAdmin()
        const prefix = '3dotmenu.page.'

        switch (key) {
            case pageMenuActions.LOCK: {
                if (!isDefaultPage) {
                    item.isVisible = false
                } else if (store?.isMasterSuper()) {
                    if (isLocked) item.tooltip = i18n.t(prefix + 'unlock.desc.master')
                    else item.tooltip = i18n.t(prefix + 'lock.desc.master')
                } else if (isLocked) {
                    item.tooltip = (i18n.t(prefix + 'lock.desc.locked'))
                    item.isDisabled = true
                } else {
                    item.isVisible = false
                }
                return item
            }
            case pageMenuActions.DELETE: {
                const hasQuestions = page?.questions?.length > 0
                if (hasQuestions) {
                    item.isDisabled = true
                    item.tooltip = i18n.t(prefix + 'delete.desc_disabled')
                    item.disabledReasons.push(i18n.t(prefix + 'delete.desc_disabled.has_questions'))
                }
                if (isLocked) {
                    item.isDisabled = true
                    item.tooltip = i18n.t(prefix + 'delete.desc_disabled')
                    item.disabledReasons.push(i18n.t(prefix + 'general.desc_disabled.locked'))
                }
                if (store?.survey?.metadata.isRapidSurvey) {
                    item.disabledReasons.push(
                        i18n.t('questionLibrary.page.infoText.delete.rapid_disabled'),
                    )
                    item.isDisabled = true
                }
                const isCompletionPage = page?.pageType === 'CO'
                const { isFirstExitPage } = this.props
                if (isCompletionPage && isFirstExitPage) {
                    item.isVisible = false
                    item.isDisabled = true
                }
                return item
            }
            case pageMenuActions.SKIP: {
                if (isLocked) {
                    item.isDisabled = true
                    item.tooltip = i18n.t(prefix + 'skip.desc_disabled')
                    item.disabledReasons.push(i18n.t(prefix + 'general.desc_disabled.locked'))
                }
                const isHidden = R.pathOr(false, ['isHidden'], page)
                if (isHidden) {
                    item.isDisabled = true
                    item.tooltip = i18n.t(prefix + 'skip.desc_disabled')
                    item.disabledReasons.push(i18n.t(prefix + 'general.desc_disabled.hidden'))
                }
                if (store?.survey?.metadata.isRapidSurvey) {
                    item.disabledReasons.push(
                        i18n.t('questionLibrary.page.infoText.logic.rapid_disabled'),
                    )
                    item.isDisabled = true
                }
                if (!item.isDisabled) {
                    item.tooltip = i18n.t(prefix + 'skip.desc_enabled')
                }
                item.isVisible = isDefaultPage
                return item
            }
            case pageMenuActions.SHOW: {
                if (isLocked) {
                    item.isDisabled = true
                    item.tooltip = i18n.t(prefix + 'show.desc_disabled')
                    item.disabledReasons.push(i18n.t(prefix + 'general.desc_disabled.locked'))
                }
                item.isVisible = isDefaultPage
                return item
            }
            case pageMenuActions.TEMPLATE: {
                const hasTemplate = !!page?.template
                if (!isAdmin && hasTemplate) {
                    item.isDisabled = true
                    item.tooltip = i18n.t(prefix + 'template.desc_disabled')
                    item.disabledReasons.push(i18n.t(prefix + 'template.desc_disabled.hasStyling'))
                }
                if (isLocked) {
                    item.isDisabled = true
                    item.tooltip = i18n.t(prefix + 'template.desc_disabled')
                    item.disabledReasons.push(i18n.t(prefix + 'general.desc_disabled.locked'))
                }
                item.isVisible = isAdmin || hasTemplate
                return item
            }
            case pageMenuActions.CHARTS: {
                const isMaxbiPage = isDefaultPage && store?.survey?.isMaxbi()
                const hasCharts = page?.pageChart?.pageQuestionChart.length > 0
                if (!isAdmin && hasCharts) {
                    item.tooltip = i18n.t(prefix + 'charts.desc_disabled')
                    item.disabledReasons.push(i18n.t(prefix + 'charts.disabled.hasCharts'))
                    item.isDisabled = true
                }
                item.isVisible = isMaxbiPage && (isAdmin || hasCharts)
                return item
            }
            case pageMenuActions.REDIRECT: {
                const isExitPage = !!options && options === 'exit'
                item.isVisible = isExitPage
                item.tooltip = i18n.t(prefix + 'redirect.desc')
                return item
            }
            default:
                return item
        }
    }

    createBaseMenu = () => {
        const {
            preview,
            routing,
            remove,
            updateURL,
            updateDelay,
            lock,
            template,
            page,
            save,
            options,
            removeRedirect,
            openChartModal,
            hidden,
            disable,
            store,
        } = this.props

        const isExitPage = options === 'exit' && page

        const menuList = (
            [
                {
                    compName: pageMenuActions.PREVIEW,
                    icon: 'fa-light fa-eye',
                    label: i18n.t('s_alt_previewpage'),
                    onClick: () => this.execMenuAction(preview),
                },
                {
                    compName: pageMenuActions.SKIP,
                    icon: 'fa-light fa-arrows-repeat',
                    label: i18n.t('questionBuilder.optionsMenu.addSkipLogic'),
                    onClick: () => this.execMenuAction(routing),
                },
                {
                    compName: pageMenuActions.LOCK,
                    icon: R.pathOr(false, ['isLocked'], page) ? 'fas fa-user-lock' : 'fal fa-user-lock',
                    label: store?.isMasterSuper()
                        ? i18n.t(R.pathOr(false, ['isLocked'], page) ? 's_alt_unlock' : 's_alt_lock')
                        : i18n.t('3dotmenu.page.lock.title.locked'),
                    onClick: () => this.execMenuAction(lock),
                },
                {
                    compName: pageMenuActions.SHOW,
                    icon: hidden ? 'fa-light fa-eye' : 'fa-light fa-eye-slash',
                    label: i18n.t(hidden ? 'Builder.pages.icons.show.title' : 'Builder.pages.icons.hide.title'),
                    onClick: () => this.execMenuAction(disable),
                },
                {
                    compName: pageMenuActions.TEMPLATE,
                    icon: 'fal fa-puzzle-piece',
                    label: i18n.t('Builder.pages.icons.template.title'),
                    onClick: () => this.execMenuAction(template),
                },
                {
                    compName: pageMenuActions.CHARTS,
                    icon: 'fal fa-chart-bar',
                    label: i18n.t('Builder.pages.icons.chart.title'),
                    onClick: () => this.execMenuAction(openChartModal),
                },
                {
                    compName: pageMenuActions.REDIRECT,
                    icon: 'fa-light fa-diagram-project',
                    label: i18n.t('s_completion_add_url'),
                    menuType: 'menu',
                    list: [
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div className="inherit-question-title">
                                <p>
                                    {i18n.t('s_completion_add_url_menu_title')}
                                </p>
                            </div>
                            <Input
                                label={i18n.t('s_completion_add_url.input.url')}
                                placeholder="www.explori.com"
                                onBlur={updateURL}
                                defaultValue={isExitPage ? page.redirectUrlSettings.redirectUrl : null}
                            />
                            <Input
                                label={i18n.t('s_completion_add_url.input.delay')}
                                placeholder="5"
                                onBlur={updateDelay}
                                defaultValue={isExitPage ? page.redirectUrlSettings.delayRedirectSec : null}
                            />

                            <Button cssClass="primary regular pull-right" onClick={() => this.execMenuAction(save)}>{i18n.t('save')}</Button>
                        </div>,
                        <div>
                            <div
                                className="option-line"
                                onClick={() => this.execMenuAction(removeRedirect)}
                            >
                                <i className="fa-light fa-circle-xmark" />
                                <p dangerouslySetInnerHTML={{ __html: i18n.t('remove') }} />
                            </div>
                        </div>,
                    ],
                    iconShow: true,
                },
                {
                    compName: pageMenuActions.DELETE,
                    label: i18n.t('3dotmenu.page.delete'),
                    icon: 'fa-light fa-circle-xmark',
                    greyBg: true,
                    onClick: () => this.execMenuAction(remove),
                },
            ]
        )
        return menuList
    }

    addUILogic = (menuItem) => {
        const { page } = this.props
        const item = this.getMenuItemConfig(menuItem.compName, page)
        return Object.assign({}, menuItem, {
            disabled: item.isDisabled,
            menuShow: item.isVisible,
            menuTooltip: item.disabledReasons.length === 0
                ? item.tooltip
                : `${item.tooltip}<br><ul>${item.disabledReasons.map((reason) => `<li>${reason}</li>`).join('')}</ul>`,
        })
    }

    getMenu = () => {
        const pageMenuOptions = this.createBaseMenu().map((opt) => this.addUILogic(opt))
            .filter((config) => config.menuShow)
        return (
            pageMenuOptions.map((config) => {
                switch (config.menuType) {
                    case 'menu':
                        return (
                            <Menu
                                cssClass="sub-menu"
                                list={config.list}
                                width="400px"
                                dir="left"
                                showTick
                                hasUniqueLast
                            >
                                <div className="option-line">
                                    <i className={config.icon} />
                                    <p dangerouslySetInnerHTML={{ __html: i18n.t(config.label) }} />
                                    {config.menuTooltip
                                        && (
                                            <HoverInfo
                                                cssClass="hover-info"
                                                desc={config.menuTooltip}
                                                dir="left"
                                            >
                                                <i className="info-icon fa-solid fa-circle-question" />
                                            </HoverInfo>
                                        )}
                                </div>
                            </Menu>
                        )
                    default:
                        return (
                            <div className="question-item">
                                <div className={`option-line ${config.disabled ? 'disabled' : ''}`} onClick={config.disabled ? null : config.onClick}>
                                    {!config.menuTooltip && (
                                        <>
                                            <i className={config.icon} />
                                            <p dangerouslySetInnerHTML={{ __html: i18n.t(config.label) }} />
                                        </>
                                    )}
                                    {config.menuTooltip && (
                                        <HoverInfo
                                            cssClass="hover-info flex"
                                            desc={config.menuTooltip}
                                            dir="left"
                                        >
                                            <i className={config.icon} />
                                            <p dangerouslySetInnerHTML={{ __html: i18n.t(config.label) }} />
                                            <i className="info-icon fa-solid fa-circle-question" />
                                        </HoverInfo>
                                    )}
                                </div>
                            </div>
                        )
                }
            })
        )
    }

    onRemove = () => {
        const { remove } = this.props

        this.menuOpen = false
        remove()
    }

    renderMenu = () => {
        const { options, isFirstExitPage, store } = this.props
        const menuOptions = this.getMenu()
        const isSurveyClosed = store?.survey.closed
        switch (options) {
            case 'page':
                return <PageMenu that={this} menuOptions={menuOptions} isSurveyClosed={isSurveyClosed}/>
            case 'exit':
                return (
                    <RedirectMenu
                        that={this}
                        menuOptions={menuOptions}
                        isFirstExitPage={isFirstExitPage}
                    />
                )
            default:
                return null
        }
    }

    handlePageNameChange = ({ value }) => {
        this.setState({ pageName: value })
    }

    render() {
        const {
            title, show, toggle, children, setPageLabel,
            canEdit, disabled, page, showDragHandle, pageHasPageLogic,
            pageHasQuestionLogic, store, hidden,
        } = this.props

        const { pageName } = this.state

        return (
            <div style={{ position: 'relative' }}>
                <div className={`question-accordion ${children.length === 0 ? 'hide-panel' : ''} ${hidden ? 'hidden-page' : ''}`}>
                    <div className={`question-accordion-header ${show ? 'bottom-bordered' : ''}`}>
                        {showDragHandle && <img src={GrabIcon} alt="Grab icon" style={{ marginLeft: 20 }} />}

                        <span className="question-accordion-header-content" onClick={toggle}>
                            <div className="chevron"><i className={`fa ${show ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true" /></div>

                            <span>{title}</span>
                            {
                                canEdit ? (
                                    <Input
                                        label="page title input"
                                        hideLabel
                                        name="page title"
                                        value={pageName}
                                        placeholder={i18n.t('s_add_page_name')}
                                        onBlur={setPageLabel}
                                        onChange={this.handlePageNameChange}
                                        style={{ maxWidth: 300, top: 7, marginLeft: 18 }}
                                    />
                                ) : <p>{pageName}</p>
                            }
                        </span>

                        {R.pathOr(false, ['isLocked'], page)
                            ? (
                                <div style={{ position: 'relative' }} className="locked-icon-container">
                                    <Tooltip direction="bottom" text={i18n.t('s_alt_islocked')} />

                                    <i className="fas fa-user-lock" style={{ marginRight: 18, marginTop: 2 }} />
                                </div>
                            )
                            : null}

                        {R.pathOr(false, ['isHidden'], page)
                            ? (
                                <div style={{ position: 'relative' }} className="locked-icon-container">
                                    <Tooltip direction="bottom" text={i18n.t('s_alt_isHidden')} />

                                    <i className="fas fa-eye-slash" style={{ marginRight: 18, marginTop: 2 }} />
                                </div>
                            )
                            : null}

                        {pageHasPageLogic
                            ? (
                                <div className="skip-logic-icon-container" onClick={() => store.showModal('pageSkipLogicPreview', { page, kind: 'showPage' })}>
                                    <Tooltip direction="bottom" text={i18n.t('displayLogicApplied')} />

                                    <i className="fal fa-arrows-repeat" />

                                    {i18n.t('displayLogic')}
                                </div>
                            )
                            : null}

                        {pageHasQuestionLogic
                            ? (
                                <div className="skip-logic-icon-container question-logic" onClick={() => store.showModal('pageSkipLogicPreview', { page, kind: 'pageToPage' })}>
                                    <Tooltip direction="bottom" text={i18n.t('pageLogicApplied')} />

                                    <i className="fal fa-arrows-repeat" />

                                    {i18n.t('pageLogic')}
                                </div>
                            )
                            : null}

                        {page?.pageType === 'CO'
                            ? (
                                <div style={{ position: 'relative' }} className="screening-completion-info-container">
                                    <Tooltip direction="bottom" text={i18n.t('s_alt_completionPage')} />
                                    <i className="fa-regular fa-check" />
                                    <p>{i18n.t('s_completionPage')}</p>
                                </div>
                            )
                            : null}

                        {page?.pageType === 'EX'
                            ? (
                                <div style={{ position: 'relative' }} className="screening-completion-info-container">
                                    <Tooltip direction="bottom" text={i18n.t('s_alt_screenedPage')} />
                                    <i className="fa-regular fa-ban" />
                                    <p>{i18n.t('s_exitPage')}</p>
                                </div>
                            )
                            : null}

                        {this.renderMenu()}
                    </div>

                    <div className={`question-accordion-content ${show ? 'content-padding' : ''}`}>
                        <AnimateHeight
                            duration={250}
                            height={show ? 'auto' : 0}
                        >
                            {children}
                        </AnimateHeight>
                    </div>
                </div>

                <div className={disabled ? 'question-accordion-cover' : ''} />
            </div>
        )
    }
}

export default PanelAccordion
